import HoldCleaningRunLifecycle from "../../../hold-cleaning-intelligence/components/HoldCleaningRunLifecycle";
import {
  HoldCleaningMockBanner,
  HoldCleaningStatusBadge,
} from "../../../hold-cleaning-intelligence/components/HoldCleaningUi";
import type {
  HoldCleaningEmailIntelligenceStatus,
  HoldCleaningRefreshRun,
} from "../../../hold-cleaning-intelligence/models/hold-cleaning.models";
import {
  getHoldCleaningEmailIntelligenceStatus,
  getHoldCleaningErrorMessage,
  getHoldCleaningRefreshRun,
  getHoldCleaningRefreshRequest,
  requestHoldCleaningRefresh,
  requestHoldCleaningEmailIntelligenceBackfill,
  retryHoldCleaningEmailIntelligenceFailures,
  retryHoldCleaningRefreshRun,
} from "../../../hold-cleaning-intelligence/services/hold-cleaning.service";
import { adminStyles } from "../../styles/admin.styles";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const TERMINAL_STATUSES = new Set(["SUCCEEDED", "FAILED_RETRYABLE", "FAILED_FINAL", "STALE", "CANCELLED"]);

const HoldCleaningDebugPanel = () => {
  const [searchParams] = useSearchParams();
  const retryRunId = searchParams.get("retryRunId");
  const [run, setRun] = useState<HoldCleaningRefreshRun | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<HoldCleaningEmailIntelligenceStatus | null>(null);
  const [failureRetryLimit, setFailureRetryLimit] = useState(10);
  const recoverableSources = (emailStatus?.failureBreakdown ?? []).reduce(
    (count, item) =>
      count +
      (item.errorCode !== "INVALID_MESSAGE_ID" &&
      (item.status === "SOURCE_UNAVAILABLE" ||
        (item.stage === "OUTLOOK_LOOKUP" && item.status === "FAILED_FINAL" && item.errorCode === "NOT_FOUND"))
        ? item.count
        : 0),
    0,
  );

  const loadEmailStatus = useCallback(async () => {
    try {
      setEmailStatus(await getHoldCleaningEmailIntelligenceStatus());
    } catch (statusError) {
      setError(getHoldCleaningErrorMessage(statusError));
    }
  }, []);

  useEffect(() => void loadEmailStatus(), [loadEmailStatus]);

  const pollRun = useCallback(async (runId: string) => {
    try {
      setRun(await getHoldCleaningRefreshRun(runId));
    } catch (pollError) {
      setError(getHoldCleaningErrorMessage(pollError));
    }
  }, []);

  useEffect(() => {
    if (!run || TERMINAL_STATUSES.has(run.status)) return;
    const timer = window.setInterval(() => void pollRun(run.runId), 10_000);
    return () => window.clearInterval(timer);
  }, [pollRun, run]);

  useEffect(() => {
    if (run && TERMINAL_STATUSES.has(run.status)) void loadEmailStatus();
  }, [loadEmailStatus, run]);

  useEffect(() => {
    if (!requestId || run) return;
    const pollRequest = async () => {
      try {
        const request = await getHoldCleaningRefreshRequest(requestId);
        if (request.runId) await pollRun(request.runId);
        if (["FAILED", "CANCELLED"].includes(request.status)) {
          setError(`Refresh request ended with status ${request.status}.`);
          setRequestId(null);
        }
      } catch (pollError) {
        setError(getHoldCleaningErrorMessage(pollError));
      }
    };
    void pollRequest();
    const timer = window.setInterval(() => void pollRequest(), 2_000);
    return () => window.clearInterval(timer);
  }, [pollRun, requestId, run]);

  const launch = async (mode: "refresh" | "backfill" | "retry-failures" | "recover-sources") => {
    const confirmation =
      mode === "recover-sources"
        ? `Recheck up to ${failureRetryLimit.toString()} unavailable emails, oldest checked first? Includes old Outlook NOT_FOUND failures. Successful outcomes are preserved. Recovered emails may incur AI costs; missing emails may remain unavailable.`
        : mode === "retry-failures"
          ? `Retry up to ${failureRetryLimit.toString()} transient email failures? Successful stages will be reused; unavailable and terminal outcomes are not reopened.`
          : mode === "backfill"
            ? "Start or continue the historical Hold Cleaning email backfill? Only missing or retryable messages are processed."
            : retryRunId
              ? `Retry Hold Cleaning run ${retryRunId}? This repeats that run with its original refresh scope.`
              : "Run the full Hold Cleaning refresh now? This reloads all sources and creates fresh AI revisions for every retained matched email.";
    if (!window.confirm(confirmation)) return;

    setBusy(true);
    setError(null);
    setRun(null);
    try {
      const request =
        mode === "recover-sources"
          ? await retryHoldCleaningEmailIntelligenceFailures(failureRetryLimit, true)
          : mode === "retry-failures"
            ? await retryHoldCleaningEmailIntelligenceFailures(failureRetryLimit)
            : mode === "backfill"
              ? await requestHoldCleaningEmailIntelligenceBackfill()
              : retryRunId
                ? await retryHoldCleaningRefreshRun(retryRunId)
                : await requestHoldCleaningRefresh();
      setRequestId(request.requestId);
      if (request.runId) await pollRun(request.runId);
      await loadEmailStatus();
    } catch (launchError) {
      setError(getHoldCleaningErrorMessage(launchError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-test="admin-hold-cleaning-debug-panel" className="min-h-0 flex-1 overflow-y-auto">
      <HoldCleaningMockBanner />
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase text-green">Hold Cleaning</p>
        <h2 className={twMerge("mt-1", adminStyles.heading.sectionTitle)}>Full data refresh</h2>
        <p className="mt-2 max-w-4xl text-sm leading-6 opacity-75">
          Requests an isolated Captain worker run, reloads every source, and reprocesses all retained matched emails.
          Only one refresh may be active. Processing uses the worker's 110-minute budget, including image
          categorization. Daily scheduled runs remain incremental.
        </p>
      </div>

      <div className={adminStyles.panel.actionGrid}>
        <section className={adminStyles.panel.safe}>
          <p className="mb-4 text-sm font-semibold uppercase text-green">Execution model</p>
          <p className="text-sm leading-6">
            There is no dry-run mode for this refresh. Use Monitoring to inspect source health, checkpoints, and prior
            outcomes.
          </p>
        </section>
        <section className={adminStyles.panel.live}>
          <p className="mb-4 text-sm font-semibold uppercase text-[#BF0002]">Live action</p>
          {retryRunId && <p className="mb-3 break-all font-mono text-xs">Retrying {retryRunId}</p>}
          <button
            type="button"
            data-test="admin-hold-cleaning-run-refresh"
            disabled={busy || Boolean(requestId && !run) || Boolean(run && !TERMINAL_STATUSES.has(run.status))}
            className={twMerge(adminStyles.button.primary, adminStyles.button.live, adminStyles.button.disabled)}
            onClick={() => void launch("refresh")}
          >
            <ExclamationTriangleIcon className="h-5 w-5" />
            {busy ? "Starting..." : retryRunId ? "Retry full refresh" : "Run full refresh"}
          </button>
        </section>
      </div>

      <section className={twMerge(adminStyles.card.surface, "mt-5 p-5")}>
        <div className="flex flex-col items-start gap-4">
          <div>
            <p className="text-sm font-semibold">Historical email intelligence</p>
            <p className="mt-2 max-w-3xl text-sm opacity-75">
              Classifies and extracts only missing Hold Conditions emails. Each invocation resumes persisted progress
              and stops within its worker budget.
            </p>
            <p className="mt-2 text-xs">
              Remaining: {emailStatus?.remainingCount ?? "-"} | Model: {emailStatus?.modelName ?? "openai/gpt-5.4-nano"}
            </p>
          </div>
          <button
            type="button"
            data-test="admin-hold-cleaning-email-backfill"
            disabled={busy || Boolean(requestId && !run) || Boolean(run && !TERMINAL_STATUSES.has(run.status))}
            className={twMerge(adminStyles.button.primary, adminStyles.button.live, adminStyles.button.disabled)}
            onClick={() => void launch("backfill")}
          >
            <ExclamationTriangleIcon className="h-5 w-5" />
            {busy ? "Starting..." : "Start/continue historical email backfill"}
          </button>
        </div>
        <div className="mt-5 flex flex-col items-start gap-4 border-t pt-5">
          <div>
            <label htmlFor="hold-cleaning-failure-retry-limit" className="text-sm font-semibold">
              Email recovery batch size
            </label>
            <p className="mt-1 max-w-3xl text-xs opacity-70">
              Transient retry resumes temporary failures. Source recovery rechecks unavailable messages and old Outlook
              lookup failures without repeating successful outcomes. Both use the batch size below.
            </p>
            <select
              id="hold-cleaning-failure-retry-limit"
              className="mt-3 h-10 border bg-transparent px-3 text-sm"
              value={failureRetryLimit}
              disabled={busy || Boolean(run && !TERMINAL_STATUSES.has(run.status))}
              onChange={(event) => setFailureRetryLimit(Number(event.target.value))}
            >
              <option value={10}>10 emails</option>
              <option value={50}>50 emails</option>
              <option value={100}>100 emails</option>
            </select>
          </div>
          <button
            type="button"
            data-test="admin-hold-cleaning-email-retry-failures"
            disabled={
              busy ||
              !emailStatus ||
              emailStatus.retryableFailureCount === 0 ||
              Boolean(requestId && !run) ||
              Boolean(run && !TERMINAL_STATUSES.has(run.status))
            }
            className={twMerge(adminStyles.button.primary, adminStyles.button.live, adminStyles.button.disabled)}
            onClick={() => void launch("retry-failures")}
          >
            <ExclamationTriangleIcon className="h-5 w-5" />
            {busy ? "Starting..." : `Retry up to ${failureRetryLimit.toString()} transient failures`}
          </button>
        </div>
        <div className="mt-4 border-t pt-4">
          <p className="mb-3 text-xs opacity-75">
            Source recovery excludes invalid message IDs and unrelated terminal AI failures. It cannot restore deleted
            mail or grant mailbox access. Continue in batches after checking the results in Monitoring.{" "}
            {recoverableSources.toLocaleString()} stored outcomes can be rechecked; recovery also needs their retained
            source metadata.
          </p>
          <button
            type="button"
            data-test="admin-hold-cleaning-email-recover-sources"
            disabled={
              busy ||
              !emailStatus ||
              recoverableSources === 0 ||
              Boolean(requestId && !run) ||
              Boolean(run && !TERMINAL_STATUSES.has(run.status))
            }
            className={twMerge(adminStyles.button.primary, adminStyles.button.live, adminStyles.button.disabled)}
            onClick={() => void launch("recover-sources")}
          >
            {busy ? "Starting..." : `Recheck up to ${failureRetryLimit.toString()} unavailable emails`}
          </button>
        </div>
      </section>

      {error && (
        <div className="mt-5 border border-[#F59692] bg-[#FFEAED] px-4 py-3 text-sm text-[#BF0002]">{error}</div>
      )}
      {(requestId || run) && (
        <section className={twMerge(adminStyles.card.surface, "mt-5 p-5")} data-test="admin-hold-cleaning-run-result">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">Refresh result</p>
              {requestId && <p className="mt-2 break-all font-mono text-xs">Request {requestId}</p>}
              {run && <p className="mt-1 break-all font-mono text-xs">Run {run.runId}</p>}
            </div>
            {run && <HoldCleaningStatusBadge value={run.status} />}
          </div>
          {run && (
            <div className="mt-4 grid gap-3 text-xs sm:grid-cols-3">
              <span>Trigger: {run.trigger}</span>
              <span>Warnings: {run.warningCount}</span>
              <span>{TERMINAL_STATUSES.has(run.status) ? "Terminal" : "Polling every 10 seconds"}</span>
            </div>
          )}
          {run?.errorSummary && <p className="mt-3 text-sm text-[#BF0002]">{run.errorSummary}</p>}
          {run && <HoldCleaningRunLifecycle run={run} />}
        </section>
      )}
    </article>
  );
};

export default HoldCleaningDebugPanel;
